if (!customElements.get('bemalt-limited-stock-pickup')) {
  customElements.define(
    'bemalt-limited-stock-pickup',
    class BemaltLimitedStockPickup extends HTMLElement {
      constructor() {
        super();
        this.requestController = null;
        this.requestGeneration = 0;
        this.drawer = null;
        this.variantId = '';
        this.onPreviewClick = this.onPreviewClick.bind(this);
        this.onRetryClick = this.onRetryClick.bind(this);
      }

      connectedCallback() {}

      disconnectedCallback() {
        this.requestController?.abort();
        this.requestController = null;
        this.requestGeneration += 1;
        this.clearContent();
      }

      setVariant(variant) {
        this.requestController?.abort();
        ++this.requestGeneration;
        this.variantId = variant?.available ? String(variant.id) : '';
        this.clearContent();
        if (!this.variantId || !this.isConnected) return;

        this.fetchAvailability(this.variantId);
      }

      fetchAvailability(variantId) {
        this.requestController?.abort();
        const generation = ++this.requestGeneration;
        this.requestController = new AbortController();
        const requestController = this.requestController;
        const rootUrl = this.dataset.rootUrl.endsWith('/') ? this.dataset.rootUrl : `${this.dataset.rootUrl}/`;
        fetch(`${rootUrl}variants/${variantId}/?section_id=pickup-availability`, { signal: requestController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Pickup availability request failed: ${response.status}`);
            return response.text();
          })
          .then((text) => {
            if (!this.isCurrentRequest(generation, variantId)) return;
            const section = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section');
            const preview = section?.querySelector('pickup-availability-preview');
            const drawer = section?.querySelector('pickup-availability-drawer');
            if (!preview) return;
            this.innerHTML = preview.outerHTML;
            if (drawer) this.installDrawer(drawer);
            this.querySelector('.pickup-availability-button')?.addEventListener('click', this.onPreviewClick);
            if (this.requestController === requestController) this.requestController = null;
          })
          .catch((error) => {
            if (error.name === 'AbortError' || !this.isCurrentRequest(generation, variantId)) return;
            if (this.requestController === requestController) this.requestController = null;
            this.renderError();
          });
      }

      onRetryClick() {
        if (!this.variantId || !this.isConnected) return;
        this.clearContent();
        this.fetchAvailability(this.variantId);
      }

      isCurrentRequest(generation, variantId) {
        return this.isConnected && generation === this.requestGeneration && this.variantId === String(variantId);
      }

      installDrawer(drawer) {
        this.removeDrawer();
        this.dataset.colorScheme.split(' ').forEach((className) => drawer.classList.add(className));
        document.body.appendChild(drawer);
        this.drawer = drawer;
      }

      onPreviewClick(event) {
        if (!this.drawer || !this.isConnected) return;
        this.drawer.show(event.currentTarget);
      }

      removeDrawer() {
        this.querySelector('.pickup-availability-button')?.removeEventListener('click', this.onPreviewClick);
        if (this.drawer?.hasAttribute('open')) this.drawer.hide();
        this.drawer?.remove();
        this.drawer = null;
      }

      clearContent() {
        this.querySelector('[data-limited-stock-pickup-retry]')?.removeEventListener('click', this.onRetryClick);
        this.removeDrawer();
        this.innerHTML = '';
      }

      renderError() {
        this.clearContent();

        const preview = document.createElement('pickup-availability-preview');
        preview.className = 'pickup-availability-preview';
        preview.setAttribute('role', 'status');
        preview.setAttribute('aria-live', 'polite');

        const info = document.createElement('div');
        info.className = 'pickup-availability-info';
        const message = document.createElement('p');
        message.className = 'caption-large';
        message.textContent = this.dataset.errorMessage;
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'pickup-availability-button link link--text underlined-link';
        retry.dataset.limitedStockPickupRetry = '';
        retry.textContent = this.dataset.retryLabel;
        retry.addEventListener('click', this.onRetryClick);

        info.append(message, retry);
        preview.append(info);
        this.append(preview);
      }
    }
  );
}

class BemaltLimitedStockController {
  constructor(productInfo) {
    this.productInfo = productInfo;
    this.sectionId = productInfo.dataset.section;
    this.status = productInfo.querySelector('[data-limited-stock-status]');
    this.price = productInfo.querySelector(`#price-${this.sectionId}`);
    this.quantity = productInfo.querySelector('[data-limited-stock-quantity]');
    this.quantityInput = productInfo.querySelector('.quantity__input');
    this.submit = productInfo.querySelector(`#ProductSubmitButton-${this.sectionId}`);
    this.variantInput = productInfo.querySelector('input.product-variant-id');
    this.pickup = productInfo.querySelector('bemalt-limited-stock-pickup');
    this.deliveryQuoteAction = productInfo.querySelector('[data-bemalt-national-shipping-quote]');
    this.pending = false;
    this.hasConnected = false;
    this.hasInteractiveSelection = false;
    this.currentVariant = null;
    this.currentState = null;
    this.deliveryOptions = [];
    this.deliveryVariant = null;
    this.deliveryQuantity = null;
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onPickerChange = this.onPickerChange.bind(this);
    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onQuantityUpdate = this.onQuantityUpdate.bind(this);
  }

  connect() {
    if (this.hasConnected) return;
    this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
    this.productInfo.addEventListener('change', this.onPickerChange, true);
    this.productInfo.addEventListener('input', this.onQuantityChange);
    this.productInfo.addEventListener('change', this.onQuantityChange);
    this.unsubscribeQuantityUpdate = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.onQuantityUpdate);
    this.hasConnected = true;
    this.deliveryOptions = this.selectedOptionLines();
    this.deliveryQuantity = this.selectedDeliveryQuantity();

    if (!this.currentState) {
      this.currentVariant = this.bootstrapVariant();
      this.render(this.currentVariant);
      return;
    }

    this.render(this.currentVariant, this.currentState);
  }

  disconnect() {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.unsubscribeQuantityUpdate?.();
    this.unsubscribeQuantityUpdate = null;
    this.productInfo.removeEventListener('change', this.onPickerChange, true);
    this.productInfo.removeEventListener('input', this.onQuantityChange);
    this.productInfo.removeEventListener('change', this.onQuantityChange);
    this.hasConnected = false;
    this.pickup?.setVariant(null);
  }

  bootstrapVariant() {
    const pickerVariant = this.readPickerVariant();
    if (pickerVariant.found) return pickerVariant.variant;

    try {
      return JSON.parse(this.productInfo.querySelector('[data-limited-stock-selected-variant]')?.textContent) || null;
    } catch {
      return null;
    }
  }

  readPickerVariant() {
    const script = this.productInfo.querySelector('variant-selects [data-selected-variant]');
    if (!script) return { found: false, variant: null };

    try {
      return { found: true, variant: JSON.parse(script.textContent) };
    } catch {
      return { found: false, variant: null };
    }
  }

  onPickerChange(event) {
    if (!event.target.closest('variant-selects') || !this.productInfo.contains(event.target)) return;
    this.deliveryOptions = this.selectedOptionLines();
    this.deliveryVariant = null;
    this.deliveryQuantity = this.selectedDeliveryQuantity();
    this.pending = true;
    this.hasInteractiveSelection = true;
    this.render(null, 'pending');
  }

  onVariantChange({ data: { sectionId, variant } }) {
    if (sectionId !== this.sectionId) return;
    this.pending = false;
    this.hasInteractiveSelection = true;
    this.currentVariant = variant || null;
    this.render(variant);
  }

  onQuantityChange(event) {
    if (!event.target.matches('.quantity__input')) return;
    this.deliveryQuantity = this.selectedDeliveryQuantity();
    this.updateDeliveryQuotePayload(this.deliveryVariant);
  }

  onQuantityUpdate() {
    queueMicrotask(() => {
      if (!this.variantMatchesSelectedOptions(this.currentVariant) || !this.pickerMatchesDeliveryOptions()) return;
      this.deliveryQuantity = this.selectedDeliveryQuantity();
      this.updateDeliveryQuotePayload(this.deliveryVariant);
    });
  }

  selectedOptionLines() {
    const picker = Array.from(this.productInfo.querySelectorAll('variant-selects')).find(
      (candidate) => candidate.isConnected && !candidate.hidden && candidate.style.display !== 'none'
    );
    if (!picker) return [];
    return Array.from(picker.querySelectorAll('select')).map((select) => {
      const label = Array.from(this.productInfo.querySelectorAll('label')).find(({ htmlFor }) => htmlFor === select.id)
        ?.textContent?.trim();
      return { label: label || select.dataset.optionName || 'Opción', value: select.value };
    });
  }

  variantMatchesSelectedOptions(variant) {
    const options = this.deliveryOptions;
    if (options.length === 0) {
      return Boolean(
        variant
        && (variant.options?.length === 0 || (variant.options?.length === 1 && variant.options[0] === 'Default Title'))
      );
    }
    return Boolean(
      variant
      && variant.options?.length === options.length
      && variant.options.every((value, index) => value === options[index]?.value)
    );
  }

  pickerMatchesDeliveryOptions() {
    const pickerOptions = this.selectedOptionLines();
    return pickerOptions.length === this.deliveryOptions.length
      && pickerOptions.every(({ value }, index) => value === this.deliveryOptions[index]?.value);
  }

  selectedDeliveryQuantity() {
    const input = this.productInfo.querySelector('.quantity__input');
    if (!input || !input.validity.valid) return null;
    return input.value;
  }

  invalidateDeliveryQuotePayload() {
    if (!this.deliveryQuoteAction) return;
    this.deliveryQuoteAction.removeAttribute('href');
    this.deliveryQuoteAction.removeAttribute('target');
    this.deliveryQuoteAction.removeAttribute('rel');
    this.deliveryQuoteAction.setAttribute('aria-disabled', 'true');
  }

  updateDeliveryQuotePayload(variant = this.currentVariant) {
    if (!this.deliveryQuoteAction) return;
    if (variant && !this.variantMatchesSelectedOptions(variant)) return;
    if (variant) this.deliveryVariant = variant;
    else this.deliveryVariant = null;
    const href = window.BemaltNationalShipping?.buildQuoteHref({
      whatsappNumber: this.deliveryQuoteAction.dataset.whatsappNumber,
      productTitle: this.deliveryQuoteAction.dataset.productTitle,
      productVendor: this.deliveryQuoteAction.dataset.productVendor,
      options: this.deliveryOptions,
      variantId: this.deliveryVariant?.id,
      quantity: this.deliveryQuantity,
      productUrl: new URL(this.productInfo.dataset.url || location.pathname, location.origin).href,
    });
    if (!href) { this.invalidateDeliveryQuotePayload(); return; }

    this.deliveryQuoteAction.href = href;
    this.deliveryQuoteAction.target = '_blank';
    this.deliveryQuoteAction.rel = 'noopener noreferrer';
    this.deliveryQuoteAction.removeAttribute('aria-disabled');
  }

  render(variant, state = null) {
    const resolvedState = state || this.resolveState(variant);
    this.currentVariant = variant || null;
    this.currentState = resolvedState;
    if (this.price) {
      if (resolvedState === 'pending' || resolvedState === 'unavailable') {
        this.price.innerHTML = '';
      }
      this.price.removeAttribute('aria-busy');
    }
    const purchasable = resolvedState === 'available';
    this.status.dataset.state = resolvedState;
    this.status.innerHTML = this.statusMarkup(resolvedState);
    this.quantity.hidden = !purchasable;
    this.quantityInput.disabled = !purchasable;
    this.submit.disabled = !purchasable;
    this.submit.setAttribute('aria-disabled', String(!purchasable));
    if (purchasable) {
      this.variantInput.value = variant.id;
      this.variantInput.disabled = false;
      this.productInfo.setQuantityBoundries?.();
    } else {
      this.variantInput.value = '';
      this.variantInput.disabled = true;
    }
    this.pickup?.setVariant(purchasable ? variant : null);
    if (this.variantMatchesSelectedOptions(variant)) this.deliveryQuantity = this.selectedDeliveryQuantity();
    this.updateDeliveryQuotePayload(variant);
  }

  resolveState(variant) {
    if (!variant && this.hasInteractiveSelection) return 'unavailable';
    if (this.productInfo.dataset.productSoldOut === 'true') return 'product-sold-out';
    if (variant) return variant.available ? 'available' : 'sold-out';
    return 'unavailable';
  }

  statusMarkup(state) {
    if (state === 'pending') return '<p>Actualizando presentación…</p>';
    if (state === 'product-sold-out') return '<p class="bemalt-limited-stock__status-heading">Producto agotado</p><p>Las existencias de este producto se han terminado y no tendremos reposición.</p>';
    if (state === 'sold-out') return '<p class="bemalt-limited-stock__status-heading">Agotado</p>';
    if (state === 'unavailable') return '<p class="bemalt-limited-stock__status-heading">Combinación no disponible</p>';
    return '';
  }
}

if (!customElements.get('bemalt-limited-stock-actions')) {
  customElements.define(
    'bemalt-limited-stock-actions',
    class BemaltLimitedStockActions extends HTMLElement {
      connectedCallback() {
        if (!this.controller) {
          const productInfo = this.closest('product-info');
          if (!productInfo) return;
          this.controller = new BemaltLimitedStockController(productInfo);
        }
        this.controller.connect();
      }

      disconnectedCallback() {
        this.controller?.disconnect();
      }
    }
  );
}
